<script lang="ts" setup>
import type { TripMonitorRoute } from "@/api/trips/dto/trip-monitor";
import { useAppStore } from "@/store/app.store";
import { computed } from "vue";
import { useDateTime } from "@/composables";
import dayjs from "dayjs";
import RouteStopTime from "@/components/times/RouteStopTime.vue";
import RouteStopSecondTimeFrame from "@/components/times/RouteStopSecondTimeFrame.vue";

const { dateTZ, formatTimeDiff } = useDateTime();

const appStore = useAppStore();

const props = defineProps<{
  route: TripMonitorRoute;
}>();

function getTimeWithTZ(time: string | undefined | null, timezone: string | undefined) {
  if (!timezone || !time) {
    return "";
  }

  return dateTZ(time, timezone);
}

const diffETA = computed(() => {
  const nextStopTime = getTimeWithTZ(props.route.nextRouteStop?.timeTo, appStore.dispatcher?.timezone);
  const ETA = getTimeWithTZ(props.route.lastCheckCall?.eta, appStore.dispatcher?.timezone);

  if (!ETA || !nextStopTime) {
    return null;
  }

  const toPrint = formatTimeDiff(nextStopTime, {
    comparedToTime: ETA,
    needSign: false,
  });

  const diffInMinutes = dayjs(ETA).diff(nextStopTime, "minutes");

  return {
    toPrint,
    diffInMinutes,
  };
});

const diffNow = computed(() => {
  const toPrint = formatTimeDiff(props.route.nextRouteStop?.timeTo, {
    needSign: false,
  });

  const diffInMinutes = dayjs().diff(props.route.nextRouteStop?.timeTo, "minutes");

  return {
    toPrint,
    diffInMinutes,
  };
});

const clasETA = computed(() => {
  if (diffETA.value === null) {
    return "";
  }

  if (diffETA.value.diffInMinutes <= 0) {
    return "border-success bg-green-lighten-5 text-success";
  } else if (diffETA.value.diffInMinutes <= 15) {
    return "bg-amber-lighten-5 text-warning border-warning";
  } else {
    return "bg-red-lighten-5 text-danger border-danger";
  }
});
</script>

<template>
  <div>
    <template v-if="props.route.nextRouteStop">
      <div class="d-flex">
        <route-stop-time
          :stop-timezone="props.route.nextRouteStop.timezone"
          :time-to="props.route.nextRouteStop.timeTo"
          :time-type="props.route.nextRouteStop.timeType"
        />

        <v-tooltip v-if="props.route.nextRouteStop?.time2To">
          <template #activator="{ props: tooltipProps }">
            <span class="text-primary font-size-12 ml-2 font-weight-500" v-bind="tooltipProps">2nd</span>
          </template>

          <route-stop-second-time-frame :route-stop="props.route.nextRouteStop" />
        </v-tooltip>
      </div>
    </template>

    <div v-if="props.route.lastCheckCall?.eta" :class="clasETA" class="eta-block font-size-12">
      <div class="font-weight-500 mb-1">
        {{ getTimeWithTZ(props.route.lastCheckCall?.eta, appStore.dispatcher?.timezone) }}
      </div>

      <div>{{ getTimeWithTZ(props.route.lastCheckCall?.eta, props.route.lastCheckCall?.timezone) }}</div>

      <div v-if="diffETA && diffETA.diffInMinutes > 0" class="font-weight-600 mt-1">
        EXPECTED DELAY {{ diffETA?.toPrint }}
        <span v-if="diffNow.diffInMinutes > 0">({{ diffNow.toPrint }} LATE)</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.eta-block {
  border-radius: 4px;
  padding: 4px 8px;
  width: max-content;
}

.eta-block.text-danger {
  color: $danger !important;
}
</style>
